import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus } from 'lucide-react';
import { showError } from '@/utils/toast';
import {
  GAME_REQUEST_DEFAULT_PLATFORMS,
  GAME_REQUEST_PLATFORM_MAX_COUNT,
  GAME_REQUEST_PLATFORM_MAX_LENGTH,
} from '@/utils/gameRequests';

interface GameRequestPlatformPickerProps {
  selected: string[];
  onChange: (platforms: string[]) => void;
  usedPlatforms: string[];
}

const isSameName = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

const GameRequestPlatformPicker: React.FC<GameRequestPlatformPickerProps> = ({ selected, onChange, usedPlatforms }) => {
  const [customInput, setCustomInput] = useState('');

  const options = useMemo(() => {
    const all = [...usedPlatforms, ...GAME_REQUEST_DEFAULT_PLATFORMS, ...selected];
    return all.filter((name, index) => all.findIndex(other => isSameName(other, name)) === index);
  }, [usedPlatforms, selected]);

  const isSelected = (name: string) => selected.some(item => isSameName(item, name));

  const toggle = (name: string) => {
    if (isSelected(name)) {
      onChange(selected.filter(item => !isSameName(item, name)));
      return;
    }
    if (selected.length >= GAME_REQUEST_PLATFORM_MAX_COUNT) {
      showError(`Legfeljebb ${GAME_REQUEST_PLATFORM_MAX_COUNT} platform adható meg.`);
      return;
    }
    onChange([...selected, name]);
  };

  const addCustom = () => {
    const name = customInput.trim().replace(/\s+/g, ' ');
    if (!name) return;
    if (!isSelected(name)) toggle(name);
    setCustomInput('');
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Input
          value={customInput}
          maxLength={GAME_REQUEST_PLATFORM_MAX_LENGTH}
          placeholder="Másik platform, pl. Epic Games"
          aria-label="Másik platform megadása"
          onChange={e => setCustomInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addCustom();
            }
          }}
        />
        <Button type="button" variant="outline" onClick={addCustom} disabled={!customInput.trim()}>
          <Plus className="h-4 w-4 mr-1" /> Hozzáad
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {options.map(name => {
          const active = isSelected(name);
          return (
            <button
              key={name}
              type="button"
              aria-pressed={active}
              onClick={() => toggle(name)}
              className={`px-3 py-1 rounded-full border text-sm transition-colors ${
                active
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-secondary text-secondary-foreground border-border hover:border-primary'
              }`}
            >
              {name}
            </button>
          );
        })}
      </div>

      <p className="text-sm text-muted-foreground">
        Kiválasztva: {selected.length > 0 ? selected.join(', ') : 'még nincs'}
      </p>
    </div>
  );
};

export default GameRequestPlatformPicker;
